import React, { useState, useEffect } from 'react';
import PickCardV2 from '../components/PickCardV2';
import { MetricChip } from '../../shared';
import './AiEdgesTodayPageV2.css';

const AiEdgesTodayPageV2 = ({ onAddToBetSlip, user }) => {
  const [picks, setPicks] = useState([]);
  const [historicalPicks, setHistoricalPicks] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPrevious, setShowPrevious] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => { loadPicks(); }, []);

  const handleAddToBetSlip = (pick) => {
    onAddToBetSlip(pick);
    setNotification('✅ Added to bet slip!');
    setTimeout(() => setNotification(null), 2000);
  };

  const loadPicks = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://sqhpzxuimundhzstmjqsr6w4dq0jyyia.lambda-url.us-east-1.on.aws/api/picks?days=7');
      const data = await res.json();
      
      if (data.picks && data.picks.length > 0) {
        // Separate pending (today's active) from settled (historical)
        const pendingPicks = data.picks
          .filter(p => p.result === 'pending')
          .map(p => ({
            id: p.pick_id,
            matchup: p.matchup,
            selection: p.selection,
            odds: p.odds,
            confidence: p.confidence,
            reasoning: p.rationale ? p.rationale[0] : '',
            game_time: p.game_time,
            date: p.date,
            result: p.result
          }));
        
        // Build historical picks from settled results
        const settledPicks = data.picks.filter(p => p.result !== 'pending');
        const historical = {};
        settledPicks.forEach(p => {
          const dateKey = p.date;
          if (!historical[dateKey]) {
            historical[dateKey] = {
              date: new Date(dateKey + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
              picks: [],
              profit: 0
            };
          }
          const isWin = p.result === 'won';
          const profit = isWin ? (p.odds > 0 ? p.odds : 100 / Math.abs(p.odds) * 100) : -100;
          historical[dateKey].picks.push({
            id: p.pick_id,
            matchup: p.matchup,
            selection: p.selection,
            odds: p.odds,
            result: isWin ? 'WIN' : 'LOSS',
            profit
          });
        });
        // Calculate record per day
        Object.keys(historical).forEach(dateKey => {
          const day = historical[dateKey];
          const wins = day.picks.filter(p => p.result === 'WIN').length;
          const losses = day.picks.filter(p => p.result === 'LOSS').length;
          day.record = `${wins}-${losses}`;
          day.profit = day.picks.reduce((sum, p) => sum + p.profit, 0);
        });
        
        setPicks(pendingPicks);
        setHistoricalPicks(historical);
        
        // Stats from API
        const wins = data.stats?.wins || 0;
        const losses = data.stats?.losses || 0;
        const totalSettled = wins + losses;
        const profit = (wins * 91) - (losses * 100);
        setStats({
          totalBets: totalSettled,
          profit,
          winRate: data.stats?.win_rate || 0,
          roi: totalSettled > 0 ? (profit / (totalSettled * 100) * 100).toFixed(1) : 0
        });
      } else {
        setPicks([]);
      }
    } catch (e) {
      console.error('Load error:', e);
      setPicks([]);
    }
    setLoading(false);
  };

  const formatCurrency = (amt) => `${amt >= 0 ? '+' : ''}$${Math.abs(amt).toFixed(0)}`;

  return (
    <div className="ai-edges-v2">
      {notification && <div className="notification">{notification}</div>}
      {/* Dashboard */}
      <div className="dashboard">
        <h2>🤖 AI Edges Performance</h2>
        <div className="dashboard-stats">
          <MetricChip label="Total Bets" value={stats.totalBets || 0} neutral />
          <MetricChip label="Profit/Loss" value={formatCurrency(stats.profit || 0)} positive={stats.profit > 0} />
          <MetricChip label="Win Rate" value={`${stats.winRate || 0}%`} positive={stats.winRate >= 52} />
          <MetricChip label="ROI" value={`${stats.roi || 0}%`} positive={stats.roi > 0} />
        </div>
      </div>

      {/* Today's Picks Header */}
      <div className="page-header">
        <h3>🎯 Today's Picks</h3>
        <span className="date-label">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </span>
      </div>

      {/* Today's Picks */}
      {loading ? (
        <div className="loading-state">Loading picks...</div>
      ) : picks.length === 0 ? (
        <div className="empty-state">
          <p>No active picks right now.</p>
          <p className="muted">Check previous picks below!</p>
        </div>
      ) : (
        <div className="picks-grid">
          {picks.map((pick, i) => (
            <PickCardV2 key={pick.id || i} pick={pick} onAdd={handleAddToBetSlip} />
          ))}
        </div>
      )}

      {/* Previous Picks Section */}
      <div className="previous-section">
        <button className="toggle-btn" onClick={() => setShowPrevious(!showPrevious)}>
          📊 Previous Picks {showPrevious ? '▲' : '▼'}
        </button>
        
        {showPrevious && (
          <div className="previous-content">
            {Object.entries(historicalPicks)
              .sort(([a], [b]) => new Date(b) - new Date(a))
              .map(([date, dayData]) => (
                <div key={date} className="history-day">
                  <div className="day-header">
                    <span className="day-date">{dayData.date}</span>
                    <span className="day-record">{dayData.record}</span>
                    <span className={`day-profit ${dayData.profit >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(dayData.profit)}
                    </span>
                  </div>
                  <div className="day-picks">
                    {dayData.picks.map(pick => (
                      <div key={pick.id} className={`history-pick ${pick.result.toLowerCase()}`}>
                        <span className="pick-matchup">{pick.matchup}</span>
                        <span className="pick-selection">{pick.selection} ({pick.odds > 0 ? '+' : ''}{pick.odds})</span>
                        <span className={`pick-result ${pick.result.toLowerCase()}`}>
                          {pick.result === 'WIN' ? '✅' : '❌'} {formatCurrency(pick.profit)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            {Object.keys(historicalPicks).length === 0 && (
              <p className="muted">No previous picks yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiEdgesTodayPageV2;
